import type { FormProps } from '../../types'
import {
  Smiley,
  SmileyBlank,
  SmileyMeh,
  SmileySad,
  SmileyWink,
} from '@phosphor-icons/react'
import cx from 'classnames'
import styles from '../form.module.css'

export interface RadioGroupHappinessInput {
  name: string
  type: string
  customControlType: 'radioGroupHappiness'
  value?: number
  options?: {
    name: string
    value: string
    Icon?: string
  }[]
  iconTheme: 'faces'
  disabled?: boolean
  validations?: string[]
  uiOptions?: {
    testId?: string
  }
}

const iconThemes: Record<string, Record<string, JSX.Element>> = {
  faces: {
    bad: <SmileySad className={styles.happinessIcons} />,
    belowAverage: <SmileyBlank className={styles.happinessIcons} />,
    average: <SmileyMeh className={styles.happinessIcons} />,
    aboveAverage: <Smiley className={styles.happinessIcons} />,
    good: <SmileyWink className={styles.happinessIcons} />,
  },
}

export default function RadioGroupHappiness({
  field,
  formProps,
}: {
  field: RadioGroupHappinessInput
  formProps: FormProps
}) {
  const options = field.options ?? []
  const iconTheme = field.iconTheme ?? 'faces'

  // Get current form value from formValues for reactive UI updates
  const currentValue = formProps.formValues[field.name] ?? field.value

  return (
    <div
      className={cx(styles.radioInputHappiness, styles.cursorDefault, 'm-auto')}
      data-testid={`${field.name}-group`}
    >
      {options.map((option) => {
        const optionValue = option.value
        const isSelected = currentValue !== undefined && Number(currentValue) === Number(optionValue)
        const icon = option.Icon ? iconThemes[iconTheme]?.[option.Icon] : null
        const optionTestId = `${field.name}-option-${optionValue}`

        return (
          <label
            htmlFor={`${field.name}-${option.name}`}
            className={cx(styles.radioInputHappinessLabel, {
              [styles.selected]: !field.disabled && isSelected,
              [styles.cursorDefault]: field.disabled,
            })}
            key={`${field.name}-${option.name}`}
            data-testid={optionTestId}
          >
            <input
              {...formProps.register(field.name, {
                required: field.validations?.includes('required'),
                valueAsNumber: true,
              })}
              type="radio"
              value={optionValue}
              disabled={field.disabled}
              className="sr-only"
              id={`${field.name}-${option.name}`}
              data-testid={`${optionTestId}-input`}
            />
            {icon && (
              <div className={styles.icon}>
                <div className={styles.iconElement}>
                  <div>{icon}</div>
                </div>
                {/* No visible label: the faces work in every campaign language (Alpha-910). */}
                <span className="sr-only">{option.name}</span>
              </div>
            )}
          </label>
        )
      })}
    </div>
  )
}
